import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { staffApi, ApiError, type QuoteListResponse } from "@/api";
import type { Quote } from "@/types";
import { Spinner } from "@/components/ui";
import { formatMoney } from "@/lib/money";
import { formatDateShort, contactName, siteAddress } from "@/lib/format";
import "./provision.css";

/** The list endpoint returns either a `{ items }` envelope or a bare array
 *  across deployments — tolerate both, like the other quote-list callers. */
function unwrap(res: QuoteListResponse | Quote[]): Quote[] {
  return Array.isArray(res) ? res : res.items ?? [];
}

/** The agreed figure for an accepted quote = the snapshotted `accepted_total`
 *  (mandatory base + the optional add-ons the client ticked). `quote_value` is
 *  the default-selection headline, so it misreports whenever the client's
 *  ticks differed from the defaults — fall back to it only when the snapshot
 *  is 0/absent (quotes accepted before `accepted_total` was recorded). */
function acceptedValue(q: Quote): number | undefined {
  return q.accepted_total ? q.accepted_total : q.quote_value;
}

/** The accepted-quote queue: every quote sitting at status "accepted",
 *  awaiting provisioning into a job. Each row deep-links to the workbench.
 *  Reuses the existing `staffApi.listQuotes` (no new list endpoint). */
export function AcceptedQueue() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    staffApi
      .listQuotes({ status: "accepted", per_page: 100 })
      .then((res) => {
        if (!cancelled) setQuotes(unwrap(res));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load accepted quotes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="quotes-list">
      <header className="quotes-list__head">
        <div>
          <h1>Accepted quotes</h1>
          <p className="quotes-list__count">{quotes.length} awaiting provisioning</p>
        </div>
      </header>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      {loading ? (
        <div className="quotes-list__loading"><Spinner label="Loading accepted quotes…" /></div>
      ) : quotes.length === 0 ? (
        <p className="quotes-list__empty">No accepted quotes awaiting provisioning.</p>
      ) : (
        <div className="quotes-table" role="table" aria-label="Accepted quotes">
          <div className="quotes-table__head provision-queue__head" role="row">
            <span role="columnheader">Quote</span>
            <span role="columnheader">Client</span>
            <span role="columnheader">Site</span>
            <span role="columnheader">Accepted</span>
            <span role="columnheader" className="num">Value</span>
          </div>
          {quotes.map((q) => (
            <Link
              key={q.id}
              to={`/staff/quotes/${q.id}/provision`}
              className="quotes-table__row provision-queue__row"
              role="row"
            >
              <span role="cell" data-label="Quote" className="quotes-table__ref numeric">
                {q.quote_number}
              </span>
              <span role="cell" data-label="Client">{contactName(q)}</span>
              <span role="cell" data-label="Site">{siteAddress(q) || q.site_postcode || "—"}</span>
              <span role="cell" data-label="Accepted">{formatDateShort(q.accepted_at)}</span>
              <span role="cell" data-label="Value" className="num numeric">
                {acceptedValue(q) != null ? formatMoney(acceptedValue(q)!, q.currency) : "—"}
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
